import React, { useState } from 'react';
import { DollarSign, Eye, EyeOff, ArrowRight, Sun, Moon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { PossibleLogo } from '../components/ui/PossibleLogo';
export function LoginPage() {
  const navigate = useNavigate();
  const {
    isDark,
    toggleTheme
  } = useTheme();
  const [email, setEmail] = useState('alex@example.com');
  const [password, setPassword] = useState('password123');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    await new Promise(r => setTimeout(r, 400));
    setLoading(false);
    navigate('/dashboard');
  };
  return <div className="min-h-screen bg-gray-50 dark:bg-dark-bg flex" data-id="element-997">
      {/* Left panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-primary-500 flex-col justify-between p-12 relative overflow-hidden" data-id="element-998">
        <div className="absolute inset-0 opacity-10" data-id="element-999">
          <div className="absolute top-20 left-20 w-64 h-64 rounded-full bg-white" data-id="element-1000" />
          <div className="absolute bottom-10 right-10 w-80 h-80 rounded-full bg-white" data-id="element-1001" />
        </div>
        <div className="relative z-10" data-id="element-1002">
          <button onClick={() => navigate('/')} className="flex items-center gap-2" data-id="element-1003">
            <PossibleLogo height={18} variant="white" data-id="element-1004" />
          </button>
        </div>
        <div className="relative z-10" data-id="element-1005">
          <h2 className="text-4xl font-black text-white mb-4" data-id="element-1006">
            Welcome back to your financial command center.
          </h2>
          <p className="text-primary-200 text-lg" data-id="element-1007">
            Your money is working hard. Let's see how it's doing.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-3" data-id="element-1008">
          <div className="flex -space-x-2" data-id="element-1009">
            {['#39E75F', '#F59E0B', '#EC4899'].map((color, i) => <div key={i} className="w-8 h-8 rounded-full border-2 border-primary-500" style={{
            backgroundColor: color
          }} data-id="element-1010" />)}
          </div>
          <p className="text-primary-200 text-sm" data-id="element-1011">
            Join 100,000+ users managing their finances smarter
          </p>
        </div>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex flex-col justify-center items-center p-6 sm:p-12" data-id="element-1012">
        <div className="w-full max-w-md" data-id="element-1013">
          {/* Mobile logo */}
          <div className="flex items-center justify-between mb-8 lg:hidden" data-id="element-1014">
            <button onClick={() => navigate('/')} className="flex items-center gap-2" data-id="element-1015">
              <div className="w-4 h-6 rounded-sm bg-gray-900 dark:bg-white shrink-0" data-id="element-1016" />
              <span className="font-bold text-gray-900 dark:text-white" data-id="element-1017">
                Possible
              </span>
            </button>
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-500" data-id="element-1018">
              {isDark ? <Sun size={17} data-id="element-1019" /> : <Moon size={17} data-id="element-1020" />}
            </button>
          </div>

          <div className="hidden lg:flex justify-end mb-6" data-id="element-1021">
            <button onClick={toggleTheme} className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-dark-hover text-gray-500" data-id="element-1022">
              {isDark ? <Sun size={17} data-id="element-1023" /> : <Moon size={17} data-id="element-1024" />}
            </button>
          </div>

          <h1 className="text-2xl font-black text-gray-900 dark:text-white mb-1" data-id="element-1025">
            Sign in
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-7" data-id="element-1026">
            Enter your credentials to access your dashboard
          </p>

          {/* Dev shortcut banner */}
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-400 text-xs px-4 py-2.5 rounded-xl mb-4 flex items-center gap-2" data-id="element-1027">
            <span className="text-base" data-id="element-1028">🧪</span>
            <span data-id="element-1029">
              <strong data-id="element-1030">Test mode:</strong> Form prefilled — just click Sign in.
            </span>
          </div>

          {error && <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl mb-4" data-id="element-1031">
              {error}
            </div>}

          <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1032">
            <div data-id="element-1033">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block" data-id="element-1034">
                Email
              </label>
              <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="alex@example.com" className="w-full bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-xl px-4 py-3 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 outline-none focus:border-primary-400 dark:focus:border-primary-500 transition-colors" data-id="element-1035" />
            </div>
            <div data-id="element-1036">
              <label className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1.5 block" data-id="element-1037">
                Password
              </label>
              <div className="relative" data-id="element-1038">
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="••••••••" className="w-full bg-white dark:bg-dark-card border border-gray-200 dark:border-dark-border rounded-xl px-4 py-3 pr-11 text-sm text-gray-800 dark:text-gray-200 placeholder-gray-400 outline-none focus:border-primary-400 dark:focus:border-primary-500 transition-colors" data-id="element-1039" />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" data-id="element-1040">
                  {showPassword ? <EyeOff size={16} data-id="element-1041" /> : <Eye size={16} data-id="element-1042" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between" data-id="element-1043">
              <label className="flex items-center gap-2 cursor-pointer" data-id="element-1044">
                <input type="checkbox" checked={rememberMe} onChange={e => setRememberMe(e.target.checked)} className="w-4 h-4 rounded border-gray-300 text-primary-500 accent-primary-500" data-id="element-1045" />
                <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1046">
                  Remember me
                </span>
              </label>
              <button type="button" className="text-sm text-primary-500 hover:text-primary-600 font-medium" data-id="element-1047">
                Forgot password?
              </button>
            </div>

            <button type="submit" disabled={loading} className="w-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-white font-semibold py-3 rounded-xl transition-all hover:scale-[1.02] active:scale-[0.98] flex items-center justify-center gap-2" data-id="element-1048">
              {loading ? <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-1049" /> : <>
                  Sign in <ArrowRight size={16} data-id="element-1050" />
                </>}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 dark:text-gray-400 mt-6" data-id="element-1051">
            Don't have an account?{' '}
            <button onClick={() => navigate('/signup')} className="text-primary-500 hover:text-primary-600 font-semibold" data-id="element-1052">
              Sign up free
            </button>
          </p>
        </div>
      </div>
    </div>;
}